import { type ChangeEvent } from 'react';
import { FiArrowRight, FiSearch } from 'react-icons/fi';
import { MdOutlineSearch } from 'react-icons/md';

import { TRACKING_NUMBER_LENGTH } from '@/constants';
import { SearchbarProps } from '@/types/props';
import { Button } from './Button';

const Searchbar = ({
  cta,
  hint,
  isButtonDisabled,
  label,
  placeholder = 'Enter tracking number...',
  register,
  title,
  variant = 'default',
}: SearchbarProps) => {
  const trackingNumberField = register('trackingNumber', {
    maxLength: TRACKING_NUMBER_LENGTH,
    minLength: TRACKING_NUMBER_LENGTH,
    required: true,
  });

  const handleTrackingNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value
      .toUpperCase()
      .slice(0, TRACKING_NUMBER_LENGTH);
    trackingNumberField.onChange(event);
  };

  const trackingInputProps = {
    ...trackingNumberField,
    autoCapitalize: 'characters' as const,
    maxLength: TRACKING_NUMBER_LENGTH,
    onChange: handleTrackingNumberChange,
    spellCheck: false,
  };

  if (variant === 'hero') {
    return (
      <div className='overflow-hidden rounded-2xl border border-white/90 bg-white/90 shadow-[0_20px_56px_rgba(9,45,54,0.09)] ring-1 ring-[#092d36]/[0.04] backdrop-blur-sm transition-all duration-300 focus-within:border-greenSecondary/30 focus-within:shadow-[0_24px_64px_rgba(68,148,134,0.13)] focus-within:ring-greenSecondary/10'>
        <div
          aria-hidden
          className='h-[2px] w-full bg-gradient-to-r from-transparent via-greenSecondary/65 to-transparent'
        />

        <div className='p-5 sm:p-6'>
          <div className='mb-5 flex flex-col items-center gap-3 text-center sm:mb-5 sm:items-start sm:text-left'>
            <div className='flex items-center gap-3'>
              <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-backgroundGreen text-white shadow-[0_4px_14px_rgba(9,45,54,0.22)]'>
                <FiSearch aria-hidden className='h-4 w-4' />
              </span>
              <div className='min-w-0 text-left'>
                {label ? (
                  <p className='text-[10px] font-semibold uppercase tracking-[0.2em] text-greenSecondary'>
                    {label}
                  </p>
                ) : null}
                {title ? (
                  <h2 className='font-headings-semibold text-base leading-snug text-backgroundGreen sm:text-lg'>
                    {title}
                  </h2>
                ) : null}
              </div>
            </div>

            {hint ? (
              <p className='max-w-md text-sm leading-relaxed text-graySecondary'>
                {hint}
              </p>
            ) : null}

            <div
              aria-hidden
              className='hidden w-full max-w-[180px] items-center gap-2 sm:flex'
            >
              <span className='h-px flex-1 bg-gradient-to-r from-greenSecondary/40 to-transparent' />
              <span className='h-1 w-1 rounded-full bg-greenSecondary/60' />
            </div>
          </div>

          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0 sm:rounded-full sm:border sm:border-gray-200/90 sm:bg-[#f8fafb] sm:p-1 sm:transition-colors sm:focus-within:border-greenSecondary/40 sm:focus-within:bg-white'>
            <div className='relative min-w-0 flex-1'>
              <FiSearch
                aria-hidden
                className='pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-greenSecondary/80 sm:left-4'
              />
              <input
                autoComplete='off'
                className='block h-11 w-full rounded-full border border-gray-200/90 bg-white pl-10 pr-4 text-sm uppercase text-gray-900 shadow-sm transition-colors placeholder:normal-case placeholder:text-gray-400 focus:border-greenSecondary/50 focus:outline-none focus:ring-2 focus:ring-greenSecondary/15 sm:h-10 sm:border-0 sm:bg-transparent sm:shadow-none sm:focus:ring-0'
                id='tracking-number'
                placeholder={placeholder}
                type='text'
                {...trackingInputProps}
              />
            </div>

            <button
              className='tracking-pill group inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-white shadow-pill transition-all duration-200 hover:brightness-105 disabled:opacity-45 sm:h-10 sm:px-6'
              disabled={isButtonDisabled}
              type='submit'
            >
              {cta}
              <FiArrowRight
                aria-hidden
                className='h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-disabled:translate-x-0'
              />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='relative flex w-full gap-2'>
      <input
        className='block w-full rounded-xl border border-gray-200/90 bg-white px-4 py-3 text-sm uppercase text-gray-900 shadow-sm transition-all duration-200 placeholder:normal-case placeholder:text-gray-400 focus:border-greenSecondary focus:outline-none focus:ring-2 focus:ring-greenSecondary/20'
        placeholder={placeholder}
        type='text'
        {...trackingInputProps}
      />

      <Button
        className='text-md flex items-center rounded-xl bg-gradient-to-br from-[#0c3a44] to-banner px-4 py-3 font-semibold text-white shadow-pill transition-all duration-200 hover:brightness-110 disabled:opacity-50'
        disabled={isButtonDisabled}
        type='submit'
      >
        <MdOutlineSearch size='1.5em' />
        {cta}
      </Button>
    </div>
  );
};

export default Searchbar;
